// components/MessageRow.js
import React, { useMemo, useCallback } from "react";
import MessageMedia from "./MessageMedia";
import Ticks from "./Ticks";

function MessageRow({
  message,
  previousMessage,

  myChatId,
  isGroupChat,
  selectedItem,
  users = [],
  groupMembers = {},

  resolveSenderChatId,
  findUsernameByChatId,
  senderColour,

  pinnedSet,
  scheduledMap,
  selectedMessages,

  mobileView,

  onRevealMedia,
  onOpenMedia,

  onRowTouchStart,
  onRowTouchMove,
  onRowTouchEnd,
  onRowTouchCancel,
  handleMouseDown,
  handleMouseUp,
  handleMouseLeave,

  renderTextWithLinks,
  renderTextWithMentions,
  handleMentionClick,

  TimeTail,
  VoiceMessageBubble,
  AvatarBubble,
  ScheduledBadge,

  onMessageForward,
  handleCancelScheduled,
}) {
  const msgId = useMemo(
    () => message.messageId || message.id || message.raw?.key?.msgId,
    [message]
  );

  const senderId = useMemo(() => {
    if (typeof resolveSenderChatId === "function") {
      return resolveSenderChatId(message);
    }
    return (
      message.sender ||
      message.senderChatId ||
      message.raw?.key?.participant ||
      message.raw?.key?.senderChatId ||
      null
    );
  }, [message, resolveSenderChatId]);

  const isSent = useMemo(() => {
    if (senderId) return String(senderId) === String(myChatId);

    if (message.fromMe === true) return true;
    if (message.fromMe === false) return false;
    if (message.raw?.key?.fromMe === true) return true;
    if (message.raw?.key?.fromMe === false) return false;

    return false;
  }, [senderId, myChatId, message]);

  const media = useMemo(
    () => message.media ?? message.raw?.message?.media ?? null,
    [message]
  );

  const handleOpenMedia = useCallback(() => {
    if (media) onOpenMedia(media);
  }, [onOpenMedia, media]);

  const handleRevealMedia = useCallback(() => {
    onRevealMedia(msgId);
  }, [onRevealMedia, msgId]);

  const text = useMemo(
    () => message.text || message.raw?.message?.conversation || "",
    [message]
  );

  const cleanText = useMemo(
    () => (typeof text === "string" ? text.trim() : ""),
    [text]
  );

  const isVoice = useMemo(
    () =>
      media?.isVoice === true ||
      media?.type === "audio" ||
      media?.mimeType?.startsWith("audio/"),
    [media]
  );

  const replyData = useMemo(() => {
    return (
      message.reply ||
      message.raw?.message?.reply ||
      (message.replyPreview
        ? {
            msgId: message.replyToMessageId,
            text:
              message.replyPreview.type === "text"
                ? message.replyPreview.value
                : null,
            media:
              message.replyPreview.type === "media"
                ? message.replyPreview.value
                : null,
          }
        : null)
    );
  }, [message]);

  const hasValidReply = useMemo(
    () => replyData && ((replyData.text && replyData.text.trim()) || replyData.media),
    [replyData]
  );

  const hasRenderableContent = useMemo(
    () => cleanText.length > 0 || !!media || hasValidReply,
    [cleanText, media, hasValidReply]
  );

  /* image / video / document (anything with media that is not a voice note) */
  const hasVisualMedia = !!media?.url && !isVoice;

  /* ── Click on the quoted reply → scroll to + flash the original message ── */
  const handleReplyClick = useCallback(
    (e) => {
      e.stopPropagation();
      if (selectedMessages.length > 0) return; // selection mode: taps select
      const targetId = replyData?.msgId;
      if (!targetId) return;

      const target =
        document.getElementById(`msg-${targetId}`) ||
        document.querySelector(`[data-alt-id="${targetId}"]`);
      if (!target) return; // original not loaded or deleted

      target.scrollIntoView({ behavior: "smooth", block: "center" });
      target.animate(
        [
          { backgroundColor: "rgba(37,211,102,0.35)" },
          { backgroundColor: "rgba(37,211,102,0)" },
        ],
        { duration: 1800, delay: 300, easing: "ease-out", fill: "backwards" }
      );
    },
    [replyData, selectedMessages]
  );

  const showSenderName = useMemo(
    () => isGroupChat && !isSent && hasRenderableContent,
    [isGroupChat, isSent, hasRenderableContent]
  );

  const senderUsername = useMemo(
    () => (showSenderName ? findUsernameByChatId(senderId) : null),
    [showSenderName, senderId, findUsernameByChatId]
  );

  const senderUser = useMemo(() => {
    if (!isGroupChat) {
      return users.find((u) => String(u.chatId) === String(senderId));
    }

    const members = groupMembers?.[selectedItem?.groupId] || [];

    return (
      users.find((u) => String(u.chatId) === String(senderId)) ||
      members.find((m) => String(m.chatId) === String(senderId))
    );
  }, [isGroupChat, users, groupMembers, selectedItem, senderId]);

  const avatarName = useMemo(
    () =>
      senderUser?.displayName ||
      senderUser?.userName ||
      String(senderId || ""),
    [senderUser, senderId]
  );

  const avatarImageUrl = useMemo(
    () => senderUser?.profileImageUrl || senderUser?.avatarUrl || null,
    [senderUser]
  );

  const previousSenderId = useMemo(() => {
    if (!previousMessage) return null;

    if (typeof resolveSenderChatId === "function") {
      return resolveSenderChatId(previousMessage);
    }

    return (
      previousMessage.sender ||
      previousMessage.senderChatId ||
      previousMessage.raw?.key?.participant ||
      previousMessage.raw?.key?.senderChatId ||
      null
    );
  }, [previousMessage, resolveSenderChatId]);

  const isSameAsPrevious = useMemo(
    () =>
      previousMessage &&
      String(previousSenderId) === String(senderId),
    [previousMessage, previousSenderId, senderId]
  );

  const showAvatarHere = useMemo(
    () => isGroupChat && !isSent && hasRenderableContent && !isSameAsPrevious,
    [isGroupChat, isSent, hasRenderableContent, isSameAsPrevious]
  );

  const showNameHere = useMemo(
    () => showSenderName && !isSameAsPrevious,
    [showSenderName, isSameAsPrevious]
  );

  /* tail only on the first bubble of a run from the same sender */
  const hasTail = !isSameAsPrevious;

  const timeLabel = useMemo(() => {
    if (!message.timestamp) return "";
    return new Date(message.timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  }, [message.timestamp]);

  const isPinned = useMemo(() => pinnedSet.has(msgId), [pinnedSet, msgId]);

  const isEdited = useMemo(() => !!message.isEdited, [message]);

  const scheduledAt = useMemo(() => {
    if (message._scheduled) {
      return scheduledMap[msgId] || message.scheduledAt || null;
    }
    return scheduledMap[msgId] || null;
  }, [message, scheduledMap, msgId]);

  const isScheduled = !!scheduledAt;

  const seenStatus = useMemo(() => {
    if (!isSent || message._scheduled) return null;

    const seen =
      message.raw?.message?.status?.seen === true ||
      !!message.raw?.message?.status?.seenAt ||
      message.raw?.message?.seen === true ||
      !!message.raw?.message?.seenAt ||
      message.seen === true ||
      !!message.seenAt;

    if (seen) return "seen";
    if (message._optimistic) return "sent";
    return "delivered";
  }, [message, isSent]);

  const isSelected = useMemo(
    () => selectedMessages.includes(msgId),
    [selectedMessages, msgId]
  );

  return (
    <div
      id={`msg-${msgId}`}
      data-alt-id={message.id || message.raw?.key?.msgId || undefined}
      data-unread={message.isUnread ? "true" : undefined}
      className={[
        "group flex w-full items-start px-3",
        isSameAsPrevious ? "mt-[2px]" : "mt-[8px]",
        isSent ? "justify-end" : "justify-start",
        isSelected ? "bg-blue-500/10 dark:bg-blue-400/20" : "",
      ].join(" ")}
      onTouchStart={
        mobileView ? (e) => onRowTouchStart(e, message, msgId) : undefined
      }
      onTouchMove={mobileView ? onRowTouchMove : undefined}
      onTouchEnd={mobileView ? onRowTouchEnd : undefined}
      onTouchCancel={mobileView ? onRowTouchCancel : undefined}
      onMouseDown={!mobileView ? () => handleMouseDown(msgId) : undefined}
      onMouseUp={!mobileView ? handleMouseUp : undefined}
      onMouseLeave={!mobileView ? handleMouseLeave : undefined}
      style={{ userSelect: "none", WebkitUserSelect: "none", WebkitTouchCallout: "none" }}
    >
      {showAvatarHere ? (
        <div className="flex-shrink-0 w-8 mr-1 pt-0.5">
          <AvatarBubble name={avatarName} imageUrl={avatarImageUrl} />
        </div>
      ) : (
        isGroupChat && !isSent && <div className="flex-shrink-0 w-8 mr-1" />
      )}

      <div
        data-bubble
        className={[
          "relative transition-all rounded-[7.5px] shadow-[0_1px_0.5px_rgba(11,20,26,0.13)]",
          isVoice
            ? "px-2 py-2 min-w-[220px]"
            : hasVisualMedia
            ? "p-[3px] max-w-[80%] sm:max-w-[65%]"
            : "px-[9px] py-[6px] max-w-[80%] sm:max-w-[65%]",
          hasTail ? (isSent ? "rounded-tr-none" : "rounded-tl-none") : "",
          isSelected
            ? "ring-2 ring-blue-500 bg-blue-100 dark:bg-blue-900/40"
            : isSent
            ? "bg-[#d9fdd3] dark:bg-[#005C4B]"
            : "bg-white dark:bg-[#202C33]",
          isPinned ? "border-l-2 border-l-green-500" : "",
          isScheduled && !isPinned ? "border-l-2 border-l-amber-400" : "",
        ].join(" ")}
        style={{ willChange: "transform" }}
      >
        {/* Bubble tail */}
        {hasTail && !isSelected && (
          <span
            aria-hidden="true"
            className={[
              "absolute top-0 w-2 h-[13px] pointer-events-none",
              isSent
                ? "-right-2 text-[#d9fdd3] dark:text-[#005C4B]"
                : "-left-2 text-white dark:text-[#202C33]",
            ].join(" ")}
          >
            <svg viewBox="0 0 8 13" width="8" height="13">
              {isSent ? (
                <>
                  <path opacity=".13" d="M5.188 1H0v11.193l6.467-8.625C7.526 2.156 6.958 1 5.188 1z" />
                  <path fill="currentColor" d="M5.188 0H0v11.193l6.467-8.625C7.526 1.156 6.958 0 5.188 0z" />
                </>
              ) : (
                <>
                  <path opacity=".13" d="M1.533 3.568 8 12.193V1H2.812C1.042 1 .474 2.156 1.533 3.568z" />
                  <path fill="currentColor" d="M1.533 2.568 8 11.193V0H2.812C1.042 0 .474 1.156 1.533 2.568z" />
                </>
              )}
            </svg>
          </span>
        )}

        {/* Swipe-to-reply icon */}
        <span
          data-swipe-icon
          aria-hidden="true"
          style={{
            position: "absolute", left: "-32px", top: "50%",
            transform: "translateY(-50%) scale(0.4)", opacity: 0,
            pointerEvents: "none", fontSize: "18px", lineHeight: 1,
            color: "#25D366", transition: "none", userSelect: "none",
          }}
        >↩</span>

        {showNameHere && senderUsername && (
          <div
            className={`text-xs font-semibold mb-1 ${hasVisualMedia ? "px-[6px] pt-1" : ""}`}
            style={{ color: senderColour(senderUsername) }}
          >
            {senderUsername}
          </div>
        )}

        {hasValidReply && (
          <div
            role="button"
            onClick={handleReplyClick}
            className="mb-1 flex overflow-hidden rounded-[6px] cursor-pointer active:opacity-80 bg-black/[0.06] dark:bg-black/20"
          >
            <div
              className="w-1 flex-shrink-0"
              style={{ backgroundColor: senderColour(replyData.senderName || "") }}
            />
            <div className="min-w-0 flex-1 px-2 py-1">
              {replyData.senderName && (
                <div
                  className="truncate text-[13px] font-medium leading-[18px]"
                  style={{ color: senderColour(replyData.senderName) }}
                >
                  {replyData.senderName}
                </div>
              )}
              <div className="line-clamp-2 break-words text-[13px] leading-[18px] text-[#667781] dark:text-neutral-400">
                           {replyData.text || (
                  <MessageMedia variant="replyLabel" media={replyData.media} />
                )}
              </div>
            </div>
                       <MessageMedia variant="replyThumb" media={replyData.media} size={52} />
          </div>
        )}

        {media?.url &&
          (isVoice ? (
            <VoiceMessageBubble
              url={media.url}
              isSent={isSent}
              timeLabel={timeLabel}
              seenStatus={seenStatus}
              isPinned={isPinned}
              isScheduled={isScheduled}
              scheduledAt={scheduledAt}
              onCancelScheduled={handleCancelScheduled}
            />
          ) : (
            <MessageMedia
              media={media}
              onOpen={handleOpenMedia}
              isRevealed
              onReveal={handleRevealMedia}
            />
          ))}

        {cleanText && (
          <div
            className={`text-[15px] leading-[20px] break-words ${
              hasVisualMedia ? "px-[6px] pt-1 pb-[6px]" : ""
            }`}
          >
            {/@\w+/.test(cleanText)
              ? renderTextWithMentions(
                  cleanText,
                  handleMentionClick,
                  renderTextWithLinks
                )
              : (
                <span
                  dangerouslySetInnerHTML={{
                    __html: renderTextWithLinks(cleanText),
                  }}
                />
              )}

            {!isVoice && (
              <TimeTail
                timeLabel={timeLabel}
                isSent={isSent}
                seenStatus={seenStatus}
                isPinned={isPinned}
                isEdited={isEdited}
                isScheduled={isScheduled}
                scheduledAt={scheduledAt}
                onCancelScheduled={handleCancelScheduled}
              />
            )}
          </div>
        )}

        {/* Time + ticks overlaid on media when there is no caption */}
        {!cleanText && hasVisualMedia && (
          <>
            <div
              aria-hidden
              className="pointer-events-none absolute inset-x-[3px] bottom-[3px] h-9 rounded-b-[6px] bg-gradient-to-t from-black/50 to-transparent"
            />
            <span className="absolute bottom-[6px] right-[9px] flex items-center gap-[3px] text-[11px] leading-none text-white">
              {isScheduled ? (
                <ScheduledBadge
                  scheduledAt={scheduledAt}
                  onCancel={handleCancelScheduled}
                />
              ) : (
                <>
                  {isPinned && <span>📌</span>}
                  {isEdited && (
                    <span className="italic opacity-80">edited</span>
                  )}
                  {timeLabel}
                  {isSent && <Ticks status={seenStatus} onMedia />}
                </>
              )}
            </span>
          </>
        )}
      </div>

      {/* Round forward button beside media bubbles */}
      {hasVisualMedia && (
        <button
          type="button"
          onTouchStart={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            onMessageForward?.(message);
          }}
          className={`self-center flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-black/25 text-white ${
            isSent ? "order-first mr-2" : "ml-2"
          }`}
          aria-label="Forward"
        >
          <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
            <path d="m14 8 6 5-6 5v-3.2c-4 0-6.6 1.2-8.6 4.2.8-4.6 3.4-8 8.6-8.8V8z" />
          </svg>
        </button>
      )}
    </div>
  );
}

export default React.memo(MessageRow, (prev, next) => {
  return (
    prev.message === next.message &&
    prev.previousMessage === next.previousMessage &&
    prev.selectedMessages === next.selectedMessages &&
    prev.pinnedSet === next.pinnedSet &&
    prev.scheduledMap === next.scheduledMap &&
    prev.myChatId === next.myChatId &&
    prev.isGroupChat === next.isGroupChat &&
    prev.mobileView === next.mobileView &&
    prev.selectedItem === next.selectedItem &&
    prev.users === next.users &&
    prev.groupMembers === next.groupMembers
  );
});